import { useNavigate } from 'react-router-dom';
import { PlusChip, plusHref } from './Plus';
import { isFitnessGoal } from '../domain/plans';
import { useStore } from '../state/store';
import { useId, useState, type ReactNode } from 'react';
import { Check } from 'lucide-react';
import { IngredientPicker } from './IngredientPicker';
import { Segmented, Field } from './ui';
import { Stepper } from './Editors';
import { CUISINE_CHOICES, CUISINE_LABELS } from '../domain/recipeMeta';
import {
  ACTIVITY_LABELS, BUDGET_LABELS, EQUIPMENT_LABELS, EXERCISE_LABELS, GOALS, HEAT_LABELS, PRIORITY_LABELS,
  SHOPPING_LABELS, SKILL_LABELS, TIME_OPTIONS, toggleGoal,
  type Activity, type Budget, type Equipment, type Exercise, type Goal, type Heat, type Priority, type Shopping, type Skill,
} from '../domain/preferences';
import { displayName, type IngredientId, labelName } from '../domain/taxonomy';
import { kgToLb, lbToKg } from '../domain/progress';

export function toggle<T>(list: T[], x: T): T[] {
  return list.includes(x) ? list.filter((y) => y !== x) : [...list, x];
}

export function Question({ title, hint, children }: { title: ReactNode; hint?: ReactNode; children: ReactNode }) {
  const id = useId();
  return (
    <fieldset className="mb-8" aria-labelledby={id}>
      <legend id={id} className="text-headline mb-1">{title}</legend>
      {hint && <p className="text-subhead text-fg-muted mb-3">{hint}</p>}
      <div className={hint ? '' : 'mt-3'}>{children}</div>
    </fieldset>
  );
}

export function ChipSet<T extends string>({ value, options, onChange, max }: {
  value: T[]; options: { id: T; label: string }[]; onChange: (v: T[]) => void; max?: number;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const on = value.includes(o.id);
        return (
          <button key={o.id} type="button" className="chip" aria-pressed={on} disabled={!on && max !== undefined && value.length >= max}
            onClick={() => onChange(toggle(value, o.id))}>{o.label}</button>
        );
      })}
    </div>
  );
}

export const CuisineChips = ({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) => (
  <ChipSet value={value} onChange={onChange} options={CUISINE_CHOICES.map((c) => ({ id: c, label: CUISINE_LABELS[c] }))} />
);

export const HeatControl = ({ value, onChange }: { value: Heat; onChange: (v: Heat) => void }) => (
  <Segmented label="Spice" value={value} onChange={onChange} options={(Object.keys(HEAT_LABELS) as Heat[]).map((h) => ({ value: h, label: HEAT_LABELS[h] }))} />
);

export const SkillControl = ({ value, onChange }: { value: Skill; onChange: (v: Skill) => void }) => (
  <Segmented label="Cooking experience" value={value} onChange={onChange} options={(Object.keys(SKILL_LABELS) as Skill[]).map((h) => ({ value: h, label: SKILL_LABELS[h] }))} />
);

export const TimeControl = ({ value, onChange }: { value: number; onChange: (v: number) => void }) => (
  <Segmented label="Weeknight cooking time" value={value} onChange={onChange} options={TIME_OPTIONS.map((t) => ({ value: t.value as number, label: t.short }))} />
);

export function DislikesEditor({ value, onChange }: { value: IngredientId[]; onChange: (v: IngredientId[]) => void }) {
  return (
    <div>
      <IngredientPicker label="Add an ingredient you don't eat" placeholder="e.g. mushrooms" exclude={value} onPick={(id) => onChange([...value, id])} />
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {value.map((d) => (
            <button key={d} type="button" className="chip" aria-label={`Remove ${displayName(d)}`} onClick={() => onChange(value.filter((x) => x !== d))}>{labelName(d)} <span aria-hidden className="text-fg-muted">✕</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export const EquipmentChips = ({ value, onChange }: { value: Equipment[]; onChange: (v: Equipment[]) => void }) => (
  <ChipSet value={value} onChange={onChange} options={(Object.keys(EQUIPMENT_LABELS) as Equipment[]).map((e) => ({ id: e, label: EQUIPMENT_LABELS[e] }))} />
);

export const ShoppingControl = ({ value, onChange }: { value: Shopping; onChange: (v: Shopping) => void }) => (
  <Segmented label="Shopping" value={value} onChange={onChange} options={(Object.keys(SHOPPING_LABELS) as Shopping[]).map((h) => ({ value: h, label: SHOPPING_LABELS[h] }))} />
);

export const BudgetControl = ({ value, onChange }: { value: Budget; onChange: (v: Budget) => void }) => (
  <Segmented label="Grocery budget" value={value} onChange={onChange} options={(Object.keys(BUDGET_LABELS) as Budget[]).map((h) => ({ value: h, label: BUDGET_LABELS[h] }))} />
);

export function HouseholdRow({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-body">Cooking for</span>
      <Stepper label="People" value={value} min={1} max={12} onChange={onChange} />
    </div>
  );
}

/**
 * Goal list. Conflicting goals (lose and gain) can't both be chosen; "No specific goal" is exclusive.
 * Fitness goals are part of Amari Plus: on the free plan they show a lock and lead to the Plus page.
 */
export function GoalsPicker({ value, onChange }: { value: Goal[]; onChange: (v: Goal[]) => void }) {
  const plus = useStore((s) => s.membership === 'plus');
  const showToast = useStore((s) => s.showToast);
  const navigate = useNavigate();
  // Stays on the page (often mid-onboarding) and offers the Plus page instead of jumping to it.
  const explainPlus = () => showToast('Fitness goals are part of Amari Plus.', { action: { label: 'See Plus', run: () => navigate(plusHref('goals')) } });
  return (
    <ul className="group" role="group" aria-label="Goals">
      {GOALS.map((g) => {
        const locked = !plus && isFitnessGoal(g.id);
        const on = value.includes(g.id) && !locked;
        return (
          <li key={g.id}>
            <button type="button" aria-pressed={on} onClick={() => (locked ? explainPlus() : onChange(toggleGoal(value, g.id)))}
              aria-label={locked ? `${g.label}, part of Amari Plus` : undefined}
              className="w-full flex items-center gap-3 px-4 py-3 min-h-[60px] text-left active:bg-fill-strong">
              <span className="flex-1">
                <span className="block text-body">{g.label}{locked && <PlusChip className="ml-2 align-middle">Plus</PlusChip>}</span>
                <span className="block text-subhead text-fg-muted">{g.detail}</span>
              </span>
              <span className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${on ? 'bg-primary text-primary-on' : 'border-2 border-fill-strong'}`} aria-hidden>
                {on && <Check size={15} strokeWidth={3} />}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export const ActivityControl = ({ value, onChange }: { value: Activity | null; onChange: (v: Activity | null) => void }) => (
  <ChipSet value={value ? [value] : []} onChange={(v) => onChange((v.filter((x) => x !== value)[0] as Activity) ?? null)}
    options={(Object.keys(ACTIVITY_LABELS) as Activity[]).map((a) => ({ id: a, label: ACTIVITY_LABELS[a] }))} />
);

export const ExerciseChips = ({ value, onChange }: { value: Exercise[]; onChange: (v: Exercise[]) => void }) => (
  <ChipSet value={value} onChange={onChange} options={(Object.keys(EXERCISE_LABELS) as Exercise[]).map((e) => ({ id: e, label: EXERCISE_LABELS[e] }))} />
);

export function PriorityPicker({ value, onChange }: { value: Priority[]; onChange: (v: Priority[]) => void }) {
  return (
    <ul className="group" role="group" aria-label="Priorities, choose up to two">
      {(Object.keys(PRIORITY_LABELS) as Priority[]).map((p) => {
        const on = value.includes(p);
        const full = !on && value.length >= 2;
        return (
          <li key={p}>
            <button type="button" aria-pressed={on} disabled={full} onClick={() => onChange(toggle(value, p))}
              className="w-full flex items-center gap-3 px-4 py-3 min-h-[56px] text-left disabled:opacity-40 active:bg-fill-strong">
              <span className="flex-1"><span className="block text-body">{PRIORITY_LABELS[p].label}</span><span className="block text-subhead text-fg-muted">{PRIORITY_LABELS[p].detail}</span></span>
              <span className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${on ? 'bg-primary text-primary-on' : 'border-2 border-fill-strong'}`} aria-hidden>
                {on && <Check size={15} strokeWidth={3} />}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/** Optional weight entry with inline validation. Stores kg; shows the user's unit. */
export function WeightInput({ units, onUnits, onSubmit, submitLabel = 'Save' }: {
  units: 'metric' | 'imperial'; onUnits: (u: 'metric' | 'imperial') => void; onSubmit: (kg: number) => void; submitLabel?: string;
}) {
  const [text, setText] = useState('');
  const [touched, setTouched] = useState(false);
  const id = useId();
  const n = Number(text.replace(',', '.'));
  const kg = units === 'metric' ? n : lbToKg(n);
  const error = !text ? null : !Number.isFinite(n) || kg < 25 || kg > 350
    ? `Enter a weight between ${units === 'metric' ? '25 and 350 kg' : `${Math.round(kgToLb(25))} and ${Math.round(kgToLb(350))} lb`}.` : null;
  return (
    <div className="grid gap-3">
      <Segmented label="Units" value={units} onChange={onUnits} options={[{ value: 'metric', label: 'kg' }, { value: 'imperial', label: 'lb' }]} />
      <Field label={`Weight (${units === 'metric' ? 'kg' : 'lb'})`} htmlFor={id} error={touched ? error : null}>
        <input id={id} className="input tabular" inputMode="decimal" autoComplete="off" value={text}
          aria-invalid={touched && !!error} aria-describedby={touched && error ? `${id}-error` : undefined}
          onChange={(e) => setText(e.target.value)} onBlur={() => setTouched(true)} placeholder={units === 'metric' ? 'e.g. 70' : 'e.g. 155'} />
      </Field>
      <button type="button" className="btn btn-secondary" disabled={!text || !!error} onClick={() => { onSubmit(kg); setText(''); setTouched(false); }}>{submitLabel}</button>
    </div>
  );
}
